import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowRight, LoaderCircle, LockKeyhole, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/AuthContext";
import { PageIntro } from "@/features/sales/storefront/components/PageIntro";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";

export const Route = createFileRoute("/productos/mis-compras")({
  head: () => ({
    meta: [
      { title: "Acceso privado a tus compras | Socrattica" },
      {
        name: "description",
        content: "Accede de forma segura a tus compras y descargas desde tu cuenta.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MyPurchasesRedirect,
});

function MyPurchasesRedirect() {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && user) {
      void navigate({ to: "/cuenta", replace: true });
    }
  }, [isLoading, navigate, user]);

  return (
    <div className="flex min-h-screen flex-col bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="flex-1">
        <PageIntro
          compact
          eyebrow="Tu biblioteca"
          title="Tus compras son privadas."
          description="Inicia sesión con el correo usado en la compra para consultar pedidos, recibos y descargas."
          details={["Cuenta verificada", "Historial privado", "Descargas seguras"]}
        />

        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          {isLoading || user ? (
            <div className="flex min-h-64 items-center justify-center" role="status">
              <LoaderCircle className="h-6 w-6 animate-spin" />
              <span className="sr-only">Abriendo tu cuenta</span>
            </div>
          ) : (
            <div className="mx-auto max-w-2xl rounded-xl border border-black/15 p-7 text-center sm:p-10">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-black text-white">
                <LockKeyhole className="h-5 w-5" />
              </span>
              <h1 className="mt-5 text-2xl font-semibold tracking-[-0.04em]">
                Accede con tu cuenta
              </h1>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-black/65">
                Ya no pedimos un correo escrito manualmente. La sesión y la verificación del correo
                protegen tu historial frente a consultas de terceros.
              </p>
              <Button asChild className="mt-6">
                <Link to="/acceso">
                  Ingresar o crear cuenta
                  <ArrowRight />
                </Link>
              </Button>{" "}
              <br />
              <p className="mt-5 inline-flex items-center gap-2 text-xs text-black/60">
                <ShieldCheck className="h-4 w-4" />
                Solo tú puedes consultar tus compras vinculadas.
              </p>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
